import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
    BASE_API_URL,
    HttpError,
} from "../../api/utils/useGovernmentAgencyHubAppQueryClient";
import { useHttpGovernmentAgencyHubAppHeaders } from "../../api/utils/useHttpGovernmentAgencyHubAppHeaders";
import type { SubscriptionDto } from "./SubscriptionDto";

// Runs in ViewWrapper, i.e. BEFORE the tenant workspace is resolved and therefore
// outside WorkspaceIdProvider: the id comes in as a parameter (from
// useTenantWorkspace) and the query stays off while it is undefined.
//
// No initialData/placeholderData: they exist to distinguish "no data yet" from
// "no active subscription". With initialData: [] a failed fetch would leave data
// at [] and ViewWrapper would show ServiceNotActive for a network error.
export function useGetSubscriptions(workspaceId: string | undefined) {
    const headers = useHttpGovernmentAgencyHubAppHeaders();
    const { t } = useTranslation();

    const query = useQuery<SubscriptionDto[]>({
        queryKey: ["useGetSubscriptions", workspaceId],
        queryFn: async () => {
            const response = await fetch(
                `${BASE_API_URL}/tenants/${workspaceId}/subscriptions`,
                {
                    headers,
                },
            );

            if (!response.ok) {
                throw new HttpError(response);
            }

            return response.json();
        },
        enabled: !!workspaceId,
        meta: {
            resourceName: t("errors.resourceName.subscriptions"),
        },
    });

    return query;
}
